import React, { useState } from 'react';
import styled from 'astroturf/react';
import { invoke } from 'js/utils/usher';
import { UsherTasks } from 'js/utils/usher/tasks';
import { Heading } from 'js/design_system/Heading';
import { Button } from 'js/design_system/Button';
import { EvaluationClaimCard } from './components/EvaluationClaimCard';

type EvaluationResult =
  UsherTasks['citeright_evaluate_publication_finder']['result'];

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-sm-24);
`;

const FormSection = styled.div`
  background: var(--background-white);
  border-radius: var(--border-radius-radius-md);
  padding: var(--spacing-sm-24);
  margin-bottom: var(--spacing-sm-24);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const FormRow = styled.div`
  display: flex;
  gap: var(--spacing-xs-16);
  align-items: center;
  margin-bottom: var(--spacing-xs-12);
`;

const Label = styled.label`
  min-width: 120px;
  font-weight: 500;
`;

const Input = styled.input`
  padding: var(--spacing-xs-8) var(--spacing-xs-12);
  border: 1px solid var(--stroke-light);
  border-radius: var(--border-radius-radius-sm);
  font-size: 14px;
  width: 150px;
`;

const StatusBanner = styled.div<{ variant: 'running' | 'complete' | 'error' }>`
  padding: var(--spacing-xs-12) var(--spacing-xs-16);
  border-radius: var(--border-radius-radius-sm);
  margin-bottom: var(--spacing-sm-24);

  &.complete {
    background: var(--brand-colors-success-100);
    color: var(--brand-colors-success-900);
  }

  &.error {
    background: var(--brand-colors-error-100);
    color: var(--brand-colors-error-900);
  }
`;

const RawJsonSection = styled.details`
  margin-top: var(--spacing-sm-24);
  background: var(--brand-colors-neutral-200);
  border-radius: var(--border-radius-radius-sm);
  padding: var(--spacing-xs-16);

  summary {
    cursor: pointer;
    font-weight: 500;
  }

  pre {
    margin-top: var(--spacing-xs-12);
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    max-height: 400px;
    overflow-y: auto;
  }
`;

type Props = {
  initialReportId?: string;
};

export const EvaluatePublicationFinderPage: React.FC<Props> = ({
  initialReportId,
}) => {
  const [reportId, setReportId] = useState(initialReportId || '');
  const [numFinalResults, setNumFinalResults] = useState(5);
  const [numVectorResults, setNumVectorResults] = useState(100);
  const [status, setStatus] = useState<
    'idle' | 'running' | 'complete' | 'error'
  >('idle');
  const [results, setResults] = useState<EvaluationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runEvaluation = async () => {
    if (!reportId) {
      setError('Please enter a report ID');
      setStatus('error');
      return;
    }

    setStatus('running');
    setError(null);
    setResults(null);

    try {
      const result = await invoke(
        'citeright_evaluate_publication_finder',
        `${reportId}:${numFinalResults}:${numVectorResults}v1`,
        {
          report_id: parseInt(reportId),
          num_final_results: numFinalResults,
          num_vector_results: numVectorResults,
        },
      );
      setResults(result);
      setStatus('complete');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      setStatus('error');
    }
  };

  return (
    <PageContainer>
      <Heading as="h1" size="lg" style={{ marginBottom: '24px' }}>
        Evaluate PublicationFinder
      </Heading>

      <FormSection>
        <Heading as="h3" size="sm" style={{ marginBottom: '16px' }}>
          Run Evaluation
        </Heading>
        <FormRow>
          <Label htmlFor="reportId">Report ID:</Label>
          <Input
            id="reportId"
            type="number"
            value={reportId}
            onChange={(e) => setReportId(e.target.value)}
            placeholder="Enter citation report ID"
          />
        </FormRow>
        <FormRow>
          <Label htmlFor="numFinalResults">Final Results:</Label>
          <Input
            id="numFinalResults"
            type="number"
            value={numFinalResults}
            onChange={(e) => setNumFinalResults(parseInt(e.target.value) || 5)}
            min={1}
            max={20}
          />
        </FormRow>
        <FormRow>
          <Label htmlFor="numVectorResults">Vector Results:</Label>
          <Input
            id="numVectorResults"
            type="number"
            value={numVectorResults}
            onChange={(e) =>
              setNumVectorResults(parseInt(e.target.value) || 100)
            }
            min={10}
            max={500}
          />
        </FormRow>
        <Button
          onClick={runEvaluation}
          disabled={status === 'running'}
          style={{ marginTop: '8px' }}
        >
          {status === 'running' ? 'Running...' : 'Run Evaluation'}
        </Button>
      </FormSection>

      {status === 'running' && (
        <StatusBanner variant="running" className="running">
          Running evaluation... This may take a few minutes.
        </StatusBanner>
      )}

      {status === 'complete' && (
        <StatusBanner variant="complete" className="complete">
          Evaluation complete!
        </StatusBanner>
      )}

      {status === 'error' && error && (
        <StatusBanner variant="error" className="error">
          Error: {error}
        </StatusBanner>
      )}

      {results && (
        <div>
          <Heading as="h2" size="md" style={{ marginBottom: '16px' }}>
            Results ({results.length} claims)
          </Heading>

          {results.map((claimResult, index) => (
            <EvaluationClaimCard
              key={claimResult.claim_id || index}
              claimResult={claimResult}
              index={index}
            />
          ))}

          <RawJsonSection>
            <summary>Raw JSON</summary>
            <pre>{JSON.stringify(results, null, 2)}</pre>
          </RawJsonSection>
        </div>
      )}
    </PageContainer>
  );
};

export default EvaluatePublicationFinderPage;
